import React, { Dispatch, SetStateAction } from "react";
import { Button } from "..";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../../../utils/cn";
import ComboboxInput from "../../inputs/ComboboxInput";
import { pageSizeOptions } from "../../../constants/common";

interface PaginationButtonsProps {
  page?: number;
  setPage?: Dispatch<SetStateAction<number>>;
  totalPages?: number;
  count?: number;
  isDarkModeStyles?: boolean;
  pageSize?: number;
  setPageSize?: Dispatch<SetStateAction<number>>;
}

const PaginationButtons = ({
  page,
  setPage,
  totalPages,
  count,
  isDarkModeStyles = true,
  pageSize,
  setPageSize,
}: PaginationButtonsProps) => {
  const { t, i18n } = useTranslation();

  return (
    <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-1">
      <div
        className={cn(
          "border border-purple-primary rounded-md px-3 max-w-max",
          isDarkModeStyles && "dark:border-white"
        )}
      >
        <p
          className={cn(
            "text-sm text-purple-primary",
            isDarkModeStyles && "dark:text-white"
          )}
        >
          {t("totalRecords")}: {count || 0}
        </p>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between sm:justify-end gap-2">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          {/* Page Info */}
          <p
            className={cn(
              "text-sm text-purple-primary",
              isDarkModeStyles && "dark:text-white"
            )}
          >
            {i18n.language === "en"
              ? `${page} of ${totalPages} pages`
              : `من  ${totalPages} ${page}`}
          </p>

          {/* Page Size Selector */}
          {pageSize && setPageSize && (
            <div className="flex items-center gap-1">
              <span
                className={cn(
                  "text-sm text-gray-600",
                  isDarkModeStyles && "dark:text-white"
                )}
              >
                {t("recordsPerPage")}:
              </span>
              <div className="w-20 mb-1">
                <ComboboxInput
                  options={pageSizeOptions}
                  value={pageSize.toString()}
                  onChange={(value) => {
                    setPageSize(Number(value));
                    if (setPage) setPage(1); // Reset to first page when changing page size
                  }}
                  isSearch={false}
                  isClear={false}
                  styles={cn(
                    "border-gray-600 py-[4.5px] rounded-[9px]",
                    isDarkModeStyles && "dark:border-white dark:!text-white "
                  )}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPage?.((prev) => prev - 1)}
            disabled={page === 1}
            className={cn(
              "px-1 sm:px-3 sm:py-2 ",
              isDarkModeStyles && "dark:border-white dark:text-white"
            )}
          >
            <span className="hidden sm:block">{t("previous")}</span>
            <ChevronLeft className="w-4 sm:hidden" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPage?.((prev) => prev + 1)}
            disabled={page === totalPages}
            className={cn(
              "px-1 sm:px-3 sm:py-2 ",
              isDarkModeStyles && "dark:border-white dark:text-white"
            )}
          >
            <span className="hidden sm:block">{t("next")}</span>
            <ChevronRight className="w-4 sm:hidden" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(PaginationButtons);
